import * as React from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  type ExternalTaskForPush,
  type PushChangeData,
  type PushChangeItem,
  PushChangesDialog,
  type PushChangeType,
} from '@/components/tasks/task-detail/PushChangesDialog';
import {
  enhancedApi as externalTaskApi,
  type ExternalTaskActionType,
  type ExternalTaskWithLinkSchema,
} from '@/services/api/generated/externalTaskService';

/** A single change request */
export interface PushChangeRequest {
  /** The type of change being pushed */
  changeType: PushChangeType;
  /** The change data to push */
  changeData: PushChangeData;
}

/** Request object for triggering push changes (single task) */
export interface PushChangesRequest {
  /** The internal task ID to push changes from */
  internalTaskId: string;
  /** One or more changes to push */
  changes: PushChangeRequest[];
  /** Optional source filter (e.g., ["FILEVINE"]) */
  sources?: string[];
}

/** Internal task info for multi-task push */
export interface InternalTaskInfo {
  id: string;
  name: string;
}

/** Request object for triggering push changes for multiple internal tasks */
export interface PushChangesMultipleRequest {
  /** List of internal tasks to push changes from */
  internalTasks: InternalTaskInfo[];
  /** One or more changes to push (applied to all tasks) */
  changes: PushChangeRequest[];
}

interface PushChangesContextValue {
  /**
   * Trigger push changes dialog for a single internal task.
   * Automatically fetches linked external tasks and filters by supported actions.
   * Resolves when user completes or skips the dialog.
   *
   * @returns Promise that resolves when the dialog closes
   */
  pushChanges: (request: PushChangesRequest) => Promise<void>;
  /**
   * Trigger push changes dialog for multiple internal tasks.
   * Fetches linked external tasks for all internal tasks, deduplicates,
   * and shows a combined dialog grouped by source internal task.
   * Resolves when user completes or skips the dialog.
   *
   * @returns Promise that resolves when the dialog closes
   */
  pushChangesMultiple: (request: PushChangesMultipleRequest) => Promise<void>;
  /**
   * Whether a push changes dialog is currently open or pending.
   */
  isPending: boolean;
}

const PushChangesContext = createContext<PushChangesContextValue | null>(null);

interface PushChangesProviderProps {
  children: React.ReactNode;
}

/**
 * Maps change type to the corresponding external task action type.
 */
function getActionTypeForChange(
  changeType: PushChangeType,
): ExternalTaskActionType {
  switch (changeType) {
    case 'status':
      return 'CHANGE_STATUS';
    case 'due_date':
      return 'CHANGE_DUE_DATE';
    case 'comment':
      return 'ADD_COMMENT';
    default:
      return 'CHANGE_STATUS';
  }
}

/**
 * Maps internal task canonical status to external task status.
 * Used to check if an external task already has the target status.
 */
function mapInternalToExternalStatus(
  canonicalStatus: string,
): 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' {
  const statusMap: Record<string, 'PENDING' | 'IN_PROGRESS' | 'COMPLETED'> = {
    Pending: 'PENDING',
    Blocked: 'PENDING',
    'In Progress': 'IN_PROGRESS',
    Completed: 'COMPLETED',
    'Not Applicable': 'COMPLETED',
    Closed: 'COMPLETED',
  };
  return statusMap[canonicalStatus] ?? 'PENDING';
}

/**
 * Filter tasks for a specific change type
 */
function filterTasksForChange(
  tasks: ExternalTaskWithLinkSchema[],
  supportedActionsMap: Map<string, ExternalTaskActionType[]>,
  changeType: PushChangeType,
  changeData: PushChangeData,
): ExternalTaskForPush[] {
  const requiredAction = getActionTypeForChange(changeType);

  // Filter tasks to those that support the required action
  let eligibleTasks = tasks.filter((task) => {
    const supportedActions = supportedActionsMap.get(task.id);
    return supportedActions?.includes(requiredAction);
  });

  // Additional filtering based on change type
  if (changeType === 'status' && changeData.status) {
    // Filter out tasks that already have the target status
    const targetStatus = mapInternalToExternalStatus(changeData.status);
    eligibleTasks = eligibleTasks.filter(
      (task) => task.status !== targetStatus,
    );
  } else if (changeType === 'due_date' && changeData.due_date) {
    // Filter out tasks that already have the target due date
    const targetDateStr = changeData.due_date.split('T')[0];
    eligibleTasks = eligibleTasks.filter((task) => {
      if (!task.due_date) return true; // No due date set, needs update
      const externalDateStr = task.due_date.split('T')[0];
      return externalDateStr !== targetDateStr;
    });
  }

  // Map to the minimal type needed by the dialog
  return eligibleTasks.map((task) => ({
    id: task.id,
    name: task.name,
    status: task.status,
    source: task.source,
  }));
}

/** External task with source internal task info for multi-task mode */
interface ExternalTaskWithSource extends ExternalTaskWithLinkSchema {
  sourceInternalTaskId: string;
  sourceInternalTaskName: string;
}

/**
 * Filter tasks for a specific change type (multi-task mode).
 * Preserves source internal task info.
 */
function filterTasksForChangeMultiple(
  tasks: ExternalTaskWithSource[],
  supportedActionsMap: Map<string, ExternalTaskActionType[]>,
  changeType: PushChangeType,
  changeData: PushChangeData,
): ExternalTaskForPush[] {
  const requiredAction = getActionTypeForChange(changeType);

  // Filter tasks to those that support the required action
  let eligibleTasks = tasks.filter((task) => {
    const supportedActions = supportedActionsMap.get(task.id);
    return supportedActions?.includes(requiredAction);
  });

  // Additional filtering based on change type
  if (changeType === 'status' && changeData.status) {
    const targetStatus = mapInternalToExternalStatus(changeData.status);
    eligibleTasks = eligibleTasks.filter(
      (task) => task.status !== targetStatus,
    );
  } else if (changeType === 'due_date' && changeData.due_date) {
    const targetDateStr = changeData.due_date.split('T')[0];
    eligibleTasks = eligibleTasks.filter((task) => {
      if (!task.due_date) return true;
      const externalDateStr = task.due_date.split('T')[0];
      return externalDateStr !== targetDateStr;
    });
  }

  // Map to the minimal type needed by the dialog, preserving source info
  return eligibleTasks.map((task) => ({
    id: task.id,
    name: task.name,
    status: task.status,
    source: task.source,
    sourceInternalTaskId: task.sourceInternalTaskId,
    sourceInternalTaskName: task.sourceInternalTaskName,
  }));
}

/**
 * Provider that enables any component to trigger push changes to external tasks.
 * Renders a single dialog instance and manages Promise resolution for each request.
 */
export const PushChangesProvider: React.FC<PushChangesProviderProps> = ({
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [changeItems, setChangeItems] = useState<PushChangeItem[]>([]);

  // Store resolve function for the current request
  const promiseRef = useRef<{
    resolve: () => void;
  } | null>(null);

  // Lazy query for fetching linked external tasks
  const [triggerFetchLinkedTasks] =
    externalTaskApi.useLazyExternalTaskByInternalTaskQuery();

  // Lazy query for fetching supported actions
  const [triggerFetchSupportedActions] =
    externalTaskApi.useLazyExternalTaskSupportedActionsQuery();

  const pushChanges = useCallback(
    (request: PushChangesRequest): Promise<void> => {
      return new Promise((resolve) => {
        const execute = async () => {
          try {
            // Fetch linked external tasks
            const linkedTasksResult = await triggerFetchLinkedTasks({
              internalTaskId: request.internalTaskId,
            }).unwrap();

            if (!linkedTasksResult || linkedTasksResult.length === 0) {
              // No linked tasks, resolve immediately
              resolve();
              return;
            }

            const filteredLinkedTasks =
              request.sources && request.sources.length > 0
                ? linkedTasksResult.filter((task) =>
                    request.sources?.includes(task.source),
                  )
                : linkedTasksResult;

            if (filteredLinkedTasks.length === 0) {
              resolve();
              return;
            }

            // Fetch supported actions for each linked task
            const supportedActionsResults = await Promise.all(
              filteredLinkedTasks.map(async (task) => {
                try {
                  const result = await triggerFetchSupportedActions({
                    id: task.id,
                  }).unwrap();
                  return {
                    id: task.id,
                    actions: result.supported_action_types,
                  };
                } catch {
                  // If fetch fails, return empty actions
                  return {
                    id: task.id,
                    actions: [] as ExternalTaskActionType[],
                  };
                }
              }),
            );

            // Create a map of task ID to supported actions
            const supportedActionsMap = new Map<
              string,
              ExternalTaskActionType[]
            >(supportedActionsResults.map(({ id, actions }) => [id, actions]));

            // Process each change request and filter eligible tasks
            const processedChanges: PushChangeItem[] = request.changes
              .map((change) => ({
                changeType: change.changeType,
                changeData: change.changeData,
                eligibleTasks: filterTasksForChange(
                  filteredLinkedTasks,
                  supportedActionsMap,
                  change.changeType,
                  change.changeData,
                ),
              }))
              .filter((change) => change.eligibleTasks.length > 0);

            if (processedChanges.length === 0) {
              // No tasks need updating for any change, resolve immediately
              resolve();
              return;
            }

            // Store the promise resolver and show the dialog
            promiseRef.current = { resolve };
            setChangeItems(processedChanges);
            setIsOpen(true);
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error fetching external tasks:', error);
            resolve();
          }
        };

        execute();
      });
    },
    [triggerFetchLinkedTasks, triggerFetchSupportedActions],
  );

  const pushChangesMultiple = useCallback(
    (request: PushChangesMultipleRequest): Promise<void> => {
      return new Promise((resolve) => {
        const execute = async () => {
          try {
            // Fetch linked external tasks for ALL internal tasks in parallel
            const allLinkedTasksResults = await Promise.all(
              request.internalTasks.map(async (internalTask) => {
                try {
                  const linkedTasks = await triggerFetchLinkedTasks({
                    internalTaskId: internalTask.id,
                  }).unwrap();
                  return {
                    internalTaskId: internalTask.id,
                    internalTaskName: internalTask.name,
                    linkedTasks: linkedTasks ?? [],
                  };
                } catch {
                  return {
                    internalTaskId: internalTask.id,
                    internalTaskName: internalTask.name,
                    linkedTasks: [] as ExternalTaskWithLinkSchema[],
                  };
                }
              }),
            );

            // Flatten and deduplicate external tasks, preserving source info
            const seenExternalTaskIds = new Set<string>();
            const allExternalTasks: ExternalTaskWithSource[] =
              allLinkedTasksResults.flatMap((result) =>
                result.linkedTasks
                  .filter((externalTask) => {
                    if (seenExternalTaskIds.has(externalTask.id)) {
                      return false;
                    }
                    seenExternalTaskIds.add(externalTask.id);
                    return true;
                  })
                  .map((externalTask) => ({
                    ...externalTask,
                    sourceInternalTaskId: result.internalTaskId,
                    sourceInternalTaskName: result.internalTaskName,
                  })),
              );

            if (allExternalTasks.length === 0) {
              // No linked tasks from any internal task
              resolve();
              return;
            }

            // Fetch supported actions for all external tasks
            const supportedActionsResults = await Promise.all(
              allExternalTasks.map(async (task) => {
                try {
                  const result = await triggerFetchSupportedActions({
                    id: task.id,
                  }).unwrap();
                  return {
                    id: task.id,
                    actions: result.supported_action_types,
                  };
                } catch {
                  return {
                    id: task.id,
                    actions: [] as ExternalTaskActionType[],
                  };
                }
              }),
            );

            const supportedActionsMap = new Map<
              string,
              ExternalTaskActionType[]
            >(supportedActionsResults.map(({ id, actions }) => [id, actions]));

            // Process each change request, filter eligible tasks, and enable grouping
            const processedChanges: PushChangeItem[] = request.changes
              .map((change) => ({
                changeType: change.changeType,
                changeData: change.changeData,
                eligibleTasks: filterTasksForChangeMultiple(
                  allExternalTasks,
                  supportedActionsMap,
                  change.changeType,
                  change.changeData,
                ),
                groupByInternalTask: true, // Enable grouping in dialog
              }))
              .filter((change) => change.eligibleTasks.length > 0);

            if (processedChanges.length === 0) {
              // No tasks need updating for any change
              resolve();
              return;
            }

            // Store the promise resolver and show the dialog
            promiseRef.current = { resolve };
            setChangeItems(processedChanges);
            setIsOpen(true);
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error fetching external tasks for multiple:', error);
            resolve();
          }
        };

        execute();
      });
    },
    [triggerFetchLinkedTasks, triggerFetchSupportedActions],
  );

  const handleClose = useCallback(() => {
    setIsOpen(false);
    promiseRef.current?.resolve();
    promiseRef.current = null;
    setChangeItems([]);
  }, []);

  const handlePushComplete = useCallback(() => {
    // Dialog handles its own closing, just resolve the promise
    // The dialog will call onClose after this
  }, []);

  const value = useMemo(
    () => ({ pushChanges, pushChangesMultiple, isPending: isOpen }),
    [pushChanges, pushChangesMultiple, isOpen],
  );

  return (
    <PushChangesContext.Provider value={value}>
      {children}
      {changeItems.length > 0 && (
        <PushChangesDialog
          open={isOpen}
          onClose={handleClose}
          onPushComplete={handlePushComplete}
          changes={changeItems}
        />
      )}
    </PushChangesContext.Provider>
  );
};

/**
 * Hook to trigger push changes to external tasks.
 * Must be used within a PushChangesProvider.
 */
export const usePushChanges = (): PushChangesContextValue => {
  const context = useContext(PushChangesContext);
  if (!context) {
    throw new Error('usePushChanges must be used within a PushChangesProvider');
  }
  return context;
};
